import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useClient, gql } from 'urql';
import posthog from 'posthog-js';
import { setInMemoryToken, silentRefresh } from '../lib/auth';

interface AuthContextType {
  accessToken: string | null;
  setAccessToken: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const LOGOUT_MUTATION = gql(`
  mutation Logout {
    logout
  }
`);

const ME_QUERY = gql(`
  query MeForIdentify {
    me { id name email }
  }
`);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Access token lives in memory only — never localStorage.
  // Recovered from httpOnly cookie via silentRefresh() on mount.
  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const client = useClient();

  // On mount: attempt silent refresh from httpOnly cookie to restore session.
  useEffect(() => {
    silentRefresh().then((token) => {
      if (token) {
        setAccessTokenState(token);
      }
      setIsInitializing(false);
    });
  }, []);

  // Proactively refresh when the tab becomes visible again after inactivity.
  // This ensures the access token is fresh before any requests fire, preventing
  // the "Invalid token" error that would otherwise surface when the expired token
  // hits the server and the reactive retry races with the UI.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        silentRefresh().then((token) => {
          if (token) setAccessTokenState(token);
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // 2. Identify the user in PostHog whenever a token is set
  useEffect(() => {
    if (!accessToken) return;
    client.query(ME_QUERY, {}).toPromise().then((result) => {
      const user = result.data?.me;
      if (user) posthog.identify(user.id, { name: user.name, email: user.email });
    });
  }, [accessToken, client]);

  // 3. Wrapper that keeps in-memory token and React state in sync
  const setAccessToken = useCallback(async (token: string | null) => {
    if (token) {
      setInMemoryToken(token);
      setAccessTokenState(token);
    } else {
      const hadToken = !!accessToken;
      posthog.reset();
      if (hadToken) {
        try {
          // Fire mutation BEFORE clearing the in-memory token so the request
          // goes out with a valid Authorization header. The server revokes DB
          // tokens and clears the httpOnly cookie via Set-Cookie.
          await client.mutation(LOGOUT_MUTATION, {}).toPromise();
        } catch {
          // ignore — logging out regardless
        }
      }
      // Clear in-memory token after the mutation so no further requests can use it.
      setInMemoryToken(null);
      setAccessTokenState(null);
      window.location.href = '/login';
    }
  }, [accessToken, client]);

  // 4. Listen for "auth:logout" event from the network layer (main.tsx)
  useEffect(() => {
    const handleLogout = () => setAccessToken(null);
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [setAccessToken]);

  if (isInitializing) return null;

  return (
    <AuthContext.Provider value={{ accessToken, setAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
